import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, MapPin, Heart, Users, BookOpen, ChevronRight } from 'lucide-react';
import { churchInfo, events } from '../mockData';

export const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[600px] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(128, 0, 32, 0.7), rgba(128, 0, 32, 0.7)), url('https://images.unsplash.com/photo-1519491050282-cf00c82424b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxiYXB0aXN0JTIwY2h1cmNofGVufDB8fHx8MTc3MTEyODIwNnww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              The Church on the Hill where everybody is somebody
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-[#fef3c7]">
              Join us for worship, grow in community, and experience God's love
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8860B] text-white font-semibold px-8 text-lg">
                  Plan Your Visit
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="bg-white text-[#800020] hover:bg-[#fffbeb] border-2 border-white font-semibold px-8 text-lg">
                  Prayer Request
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Welcome to Our Church Family</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="border-[#800020] border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-2">Welcoming Community</h3>
                <p className="text-gray-600">
                  Experience genuine fellowship in a loving, Christ-centered environment where everyone belongs.
                </p>
              </CardContent>
            </Card>

            <Card className="border-[#800020] border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-2">Biblical Teaching</h3>
                <p className="text-gray-600">
                  Grow in faith through powerful, practical preaching rooted in God's Word.
                </p>
              </CardContent>
            </Card>

            <Card className="border-[#800020] border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-2">Active Ministries</h3>
                <p className="text-gray-600">
                  Find your place to serve and grow through diverse ministries for all ages.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Times */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Join Us for Worship</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">We'd love to see you this week!</p>
          </div>

          <div className="grid md:grid-cols-1 gap-8 max-w-2xl mx-auto">
            {churchInfo.serviceTimes.map((schedule, index) => (
              <Card key={index} className="bg-white hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#800020] mb-2">{schedule.day}</h3>
                  <p className="text-[#D4AF37] font-semibold mb-4">{schedule.note}</p>
                  {schedule.services.map((service, idx) => (
                    <div key={idx} className="flex items-center gap-3 mb-4 last:mb-0">
                      <Clock className="h-5 w-5 text-[#D4AF37]" />
                      <div>
                        <div className="font-semibold text-[#800020]">{service.name}</div>
                        <div className="text-gray-600">{service.time}</div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pastor's Welcome */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={churchInfo.pastor.image} 
                alt={churchInfo.pastor.name}
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-[#800020] mb-2">A Word from Our Pastor</h2>
              <div className="w-24 h-1 bg-[#D4AF37] mb-6"></div>
              <h3 className="text-2xl font-semibold text-[#D4AF37] mb-4">
                {churchInfo.pastor.name}
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                {churchInfo.pastor.welcomeMessage}
              </p>
              <Link to="/about">
                <Button className="bg-[#800020] hover:bg-[#8B0000] text-white">
                  Learn More About Us
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Upcoming Events</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 3).map((event) => (
              <Card key={event.id} className="bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Calendar className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-[#800020] mb-1">{event.title}</h3>
                      <div className="text-sm text-gray-600 mb-2">{event.date} • {event.time}</div>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-3">{event.description}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 text-[#D4AF37]" />
                    <span>{event.location}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/services">
              <Button className="bg-[#D4AF37] hover:bg-[#B8860B] text-white">
                View All Events
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Location Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Visit Us</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">{churchInfo.address}</p>
            <p className="text-gray-600 mt-2">{churchInfo.phone}</p>
          </div>

          <div className="rounded-lg overflow-hidden shadow-xl">
            <iframe
              title="Church Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3401.234!2d-84.2324!3d31.7219!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDQzJzE4LjgiTiA4NMKwMTMnNTYuNiJX!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#800020] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Take the Next Step?</h2>
          <p className="text-xl text-[#fef3c7] mb-8">
            Whether you're visiting for the first time or looking for a church home, we're here for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8860B] text-white font-semibold px-8">
                Contact Us
              </Button>
            </Link>
            <Link to="/give">
              <Button size="lg" variant="outline" className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-[#800020] font-semibold px-8">
                Give Online
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
